'use client'

import { useState, useRef, useCallback } from 'react'
import { Send } from 'lucide-react'

interface BigglesInputProps {
  onSend: (message: string) => void
  disabled?: boolean
}

export function BigglesInput({ onSend, disabled }: BigglesInputProps) {
  const [value, setValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const handleSend = useCallback(() => {
    const trimmed = value.trim()
    if (!trimmed || disabled) return
    onSend(trimmed)
    setValue('')
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }, [value, disabled, onSend])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleInput = () => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`
  }

  return (
    <div className="shrink-0 p-3 border-t border-border bg-muted/40">
      <div className="flex items-end gap-2 rounded-lg bg-background border border-input focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30 transition-colors pl-3.5 pr-1.5 py-1.5">
        <textarea
          ref={textareaRef}
          aria-label="Ask SOLO"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          placeholder="Message SOLO..."
          disabled={disabled}
          rows={1}
          className="flex-1 resize-none bg-transparent py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:opacity-50"
        />
        <button aria-label="Send message"
          type="button"
          onClick={handleSend}
          disabled={disabled || !value.trim()}
          className="shrink-0 h-8 w-8 rounded-md flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary-hover disabled:opacity-40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Send className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
